'use client';

import { useEffect, useRef } from 'react';

const GA_PLACEHOLDER_ID = 'G-XXXXXXXXXX';

interface GoogleAnalyticsProps {
  gaId: string;
}

export function GoogleAnalytics({ gaId }: GoogleAnalyticsProps) {
  const injectedRef = useRef(false);

  useEffect(() => {
    // Skip placeholder ID (e.g. when VITE_GA_ID is not set)
    if (!gaId || gaId === GA_PLACEHOLDER_ID) return;

    // Inject only once (avoids duplicates from React Strict Mode and prevents cleanup from breaking GA)
    if (injectedRef.current) return;
    if (typeof document === 'undefined' || document.getElementById('google-analytics-config')) {
      injectedRef.current = true;
      return;
    }

    const consentScript = document.createElement('script');
    consentScript.id = 'google-analytics-consent-default';
    consentScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      var consent = localStorage.getItem('analytics-consent');
      if (consent === 'accepted') {
        gtag('consent', 'update', {
          analytics_storage: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        });
      }
    `;
    document.head.appendChild(consentScript);

    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(gtagScript);

    const configScript = document.createElement('script');
    configScript.id = 'google-analytics-config';
    configScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gaId}', {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true,
      });
    `;
    document.head.appendChild(configScript);

    injectedRef.current = true;
    // Do not remove scripts on cleanup: the gtag script loads asynchronously and
    // would be removed before loading in React Strict Mode, breaking GA entirely.
  }, [gaId]);

  return null;
}
